/**
 * This will be used within filter list groups for the PLP/Brand/Search Results pages. This will contain a list wrapper like &lt;li&gt;.
 *
 * @module views/Text/Molecules/FilterListItem
 * @memberof -Common
 */
import './FilterListItem.scss';

import React, { useRef } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import RadioButton from '@ulta/core/components/RadioButton/RadioButton';
import Text from '@ulta/core/components/Text/Text';
import UseKeyPress from '@ulta/core/hooks/useKeyPress/UseKeyPress';
import constants from '@ulta/core/utils/constants/constants';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { highlightUnmatchedText } from '@ulta/core/utils/highlightUnmatchedTextDecoration/highlightUnmatchedTextDecoration';
import { isSafeNumber } from '@ulta/core/utils/types/types';

import { useFiltersColumnContext } from '@ulta/providers/FiltersColumnProvider/FiltersColumnProvider';

import { FILTER_GROUPS, FILTER_TYPES } from '@ulta/utils/constants/filters';

import LinkCheckbox from '../LinkCheckbox/LinkCheckbox';
import MinMax from '../MinMax/MinMax';
import ReviewStarsCard from '../ReviewStarsCard/ReviewStarsCard';
import Swatch, { reduceSwatchProps } from '../Swatch/Swatch';
import * as utils from './FilterListItem';

/**
  * Represents a FilterListItem component
  *
  * @method
  * @param {FilterListItemProps} props - React properties passed from composition
  * @returns FilterListItem
  */
export const FilterListItem = function( props ){
  const { loading, toggleFilterGroupListItem } = useFiltersColumnContext();
  const {
    id,
    title,
    count,
    selected,
    disabled,
    selectAction,
    removeAction,
    minLabel,
    maxLabel,
    labelPositionLeft,
    type,
    colorCode,
    colorImage,
    minAmount,
    maxAmount,
    currencySymbol,
    availableCountLabel,
    groupType,
    applyFilterSEOUrl,
    hideItems,
    searchValue,
    componentKey,
    store
  } = props;

  const isDisabled = disabled || loading;
  const isPriceRangeFilter = type?.toUpperCase() === FILTER_TYPES.RANGE;
  const isRatingsFilter = groupType?.toUpperCase() === FILTER_GROUPS.RATING;
  const isStoreFilter = groupType?.toUpperCase() === FILTER_GROUPS.AVAILABLE_STORE_IDS;
  const isDateFilter = groupType?.toUpperCase() === FILTER_GROUPS.DATE;
  const isCheckBox = !isPriceRangeFilter && !isRatingsFilter && !isStoreFilter && !isDateFilter;
  const keyPressed = useRef( false );
  const refocusTarget = useRef();
  const liRef = useRef();
  const hrefValue = count ? applyFilterSEOUrl : '#';

  UseKeyPress( [constants.ENTER_KEY, constants.SPACE_KEY], liRef, ()=>{
    keyPressed.current = true;
  }, [constants.ENTER_KEY, constants.SPACE_KEY], true );

  const colorImageDimensions = {
    height: 32,
    width: 32
  };

  const swatchProps = reduceSwatchProps( { colorCode, colorImage, colorImageDimensions } );
  const hideFilterItems = hideItems && !isPriceRangeFilter;
  // Prop checks
  if(
    !type || ( !isPriceRangeFilter && !isSafeNumber( count ) && !title ) ||
    typeof toggleFilterGroupListItem !== 'function'
  ){
    return null;
  }

  const dataCaptureData = !selected ? selectAction?.dataCaptureData : removeAction?.dataCaptureData;

  const action = !selected ? selectAction : removeAction;

  const getTextData = {
    label: title,
    searchString: searchValue,
    highlightColor: 'black'
  };

  const storeId = store?.bopisStoreId;

  const label = (
    <>
      { !isPriceRangeFilter &&
      <>
        { swatchProps && <Swatch { ...spreadProps( { ...swatchProps } ) } /> }
        <span className='FilterListItem__name'>
          <Text
            textStyle='body-1'
            htmlTag='span'
          >
            { highlightUnmatchedText( getTextData ) }
          </Text>
        </span>
        <span
          className='sr-only'
          data-nosnippet='true'
        >
          { availableCountLabel }
        </span>
        <Text
          htmlTag='span'
          textStyle='body-3'
          color='neutral-600'
          ariaHidden={ true }
          noSnippet={ true }
        >
          { !count ? '0' : count }
        </Text>
      </>
      }
      { isPriceRangeFilter &&
        <MinMax
          id={ id }
          ref={ refocusTarget }
          label={ currencySymbol }
          isChecked={ selected }
          isDisabled={ isDisabled }
          minPlaceholder={ minLabel }
          maxPlaceholder={ maxLabel }
          labelPositionLeft={ labelPositionLeft }
          onApplyFiltersClick={ onMinMaxFilterApply( {
            toggleFilterGroupListItem
          },
          {
            filterItem: { ...props },
            keyPressed,
            refocusTarget
          } ) }
          selectAction={ selectAction }
          minAmount={ minAmount }
          maxAmount={ maxAmount }
        />
      }
    </>
  );

  return (
    <li
      ref={ liRef }
      className={
        classNames(
          'FilterListItem', {
            'FilterListItem--disabled': props.disabled,
            'FilterListItem--hide':hideFilterItems,
            'FilterListItem__store': isStoreFilter
          }
        )
      }
    >
      {
        isPriceRangeFilter && label
      }
      {
        isCheckBox && (
          <LinkCheckbox
            disabled={ isDisabled }
            id={ utils.removeSpaces( { itemId: id, groupType } ) }
            name={ title }
            ref={ refocusTarget }
            onChange={ onFilterListItemClick( {
              toggleFilterGroupListItem
            },
            {
              refocusTarget,
              keyPressed,
              filterItem: { ...props }
            }
            ) }
            checked={ !!selected }
            dataCaptureData={ dataCaptureData }
            href={ hrefValue }
          >
            { label }
          </LinkCheckbox>
        )
      }
      {
        isRatingsFilter && (
          <RadioButton
            name='primary'
            id={ id }
            isChecked={ !!selected }
            value={ id }
            action={ action }
            onChange={ onFilterListItemClick( {
              toggleFilterGroupListItem
            },
            {
              refocusTarget,
              keyPressed,
              filterItem: { ...props }
            }
            ) }
          >
            <div className='FilterListItem__initSection'>
              <span className='sr-only'>{ groupType }</span>
              <ReviewStarsCard
                rating={ Number( id ) }
                size='m'
                componentKey={ componentKey }
              />
              <span className='FilterListItem__label'>{ label }</span>
            </div>
          </RadioButton>
        )
      }
      {
        isDateFilter && (
          <RadioButton
            name='primary'
            id={ id }
            isChecked={ !!selected }
            value={ id }
            isDisabled={ isDisabled }
            action={ action }
            onChange={ onFilterListItemClick( {
              toggleFilterGroupListItem
            },
            {
              refocusTarget,
              keyPressed,
              filterItem: { ...props }
            }
            ) }
          >
            <Text htmlTag='span'
              color={ ( isDisabled || !storeId ) ? 'neutral-400' : 'neutral-800' }
            > { label }</Text>
          </RadioButton>
        )
      }
      {
        isStoreFilter && (
          <LinkCheckbox
            disabled={ isDisabled || !storeId }
            id={ id }
            name={ title }
            ref={ refocusTarget }
            onChange={ onFilterListItemClick( {
              toggleFilterGroupListItem
            },
            {
              refocusTarget,
              keyPressed,
              filterItem: {
                ...props,
                storeId: storeId
              }
            }
            ) }
            checked={ !!selected }
            dataCaptureData={ dataCaptureData }
            href={ hrefValue }
          >
            <Text htmlTag='span'
              color={ ( isDisabled || !storeId ) ? 'neutral-400' : 'neutral-800' }
            >{ title }</Text>
          </LinkCheckbox>
        )
      }
    </li>
  );
};

/**
  * @method onFilterListItemClick
  * @summary Filter list item click handler
  * @param {object} methods - passing setIsChecked, toggleFilterGroupListItem functions
  * @param {object} data - passing filterItem
  * @returns {undefined}
  */
export const onFilterListItemClick = ( methods, data ) => () => {
  const { toggleFilterGroupListItem } = methods || {};
  const { filterItem, refocusTarget, keyPressed } = data || {};

  if( methods && data ){

    const filterItemPayload = { ...filterItem };

    if( keyPressed?.current ){
      keyPressed.current = false;
      filterItemPayload.refocusTarget = refocusTarget;
    }

    toggleFilterGroupListItem( filterItemPayload );
  }
};

/**
  * @method onMinMaxFilterApply
  * @summary Range filter apply handler
  * @param {object} methods - passing toggleFilterGroupListItem function
  * @param {object} data - passing filterItem data
  * @returns {undefined}
  */
export const onMinMaxFilterApply = ( methods, data ) => ( minMaxValues )=> ( () => {
  const { toggleFilterGroupListItem } = methods || {};
  const { filterItem, refocusTarget, keyPressed } = data || {};
  if( methods && data ){

    const filterItemPayload = {
      ...filterItem,
      id: `price-range-${minMaxValues.min}-${minMaxValues.max}`,
      minAmount: minMaxValues.min,
      maxAmount: minMaxValues.max
    };

    if( keyPressed?.current ){
      keyPressed.current = false;
      filterItemPayload.refocusTarget = refocusTarget;
    }

    toggleFilterGroupListItem( filterItemPayload );
  }
} )();

/**
 * removing space from id's and appending index value to get unique id if duplicate id's repeat
 *
 * @method removeSpaces
 * @param {object} data - argument of data items
 * @returns {string} returns unique id value as string
 */
export const removeSpaces = ( data ) => {
  const { itemId, groupType } = data || {};
  let newItemId = itemId?.replace( /\s+/g, '_' );
  let uniqueId = newItemId && `${newItemId}_${groupType}`;
  return uniqueId;
};

/**
  * Property type definitions
  * @typedef FilterListItemProps
  * @type {object}
  * @property {string} id - filter id used by adeptmind
  * @property {string} title - Sets the text of the filter
  * @property {number} count -Sets the count of the filter
  * @property {string} type - Sets the type of list can be single, multi or range
  * @property {bool} selected - boolean if filter list item is selected or not
  * @property {bool} disabled - Disabled the list and component
  * @property {object} selectAction - action containing label and url when filter item selected
  * @property {object} removeAction - action containing label and url when filter item deselected
  * @property {string} minLabel - placeholder for min label in price range
  * @property {string} maxLabel - placeholder for max label in price range
  * @property {string} removeFilterAccessibility - accessibilty text while filter is deselected
  * @property {string} rangeCurrency - sets currency for price range filter
  * @property {string} currenySymbol - sets currency for price range filter
  * @property {bool} labelPositionLeft - bool to decide about position on left or right of price
  * @property {string} colorCode - color code for Swatch used in color filter
  * @property {object} colorImage - color Image for Swatch of clear or multi color
  * @property {boolean} loading - loading state
  * @property {function} toggleFilterGroupListItem - callback for toggling a filtering
  * @property {string} searchValue - searched value in filters
  * @property {object} store - Store stored in local storage from UserContext
  */
export const propTypes = {
  id: PropTypes.string,
  title: PropTypes.string,
  count: PropTypes.number,
  type: PropTypes.oneOf( ['SINGLE', 'MULTI', 'RANGE'] ).isRequired,
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
  selectAction: PropTypes.object,
  removeAction: PropTypes.object,
  minLabel: PropTypes.string,
  maxLabel: PropTypes.string,
  removeFilterAccessibility: PropTypes.string,
  rangeCurrency: PropTypes.string,
  currencySymbol: PropTypes.string,
  labelPositionLeft: PropTypes.bool,
  groupType: PropTypes.string,
  colorCode: PropTypes.string,
  loading: PropTypes.bool,
  colorImage: PropTypes.object,
  toggleFilterGroupListItem: PropTypes.func,
  searchValue: PropTypes.string,
  store: PropTypes.object
};

FilterListItem.propTypes = propTypes;

export default FilterListItem;